import React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import {
  FormLabel,
  Paper,
  Box,
  Radio,
  Typography,
  FormControl,
  FormHelperText,
} from '@mui/material';
import PropTypes from 'prop-types';

const DetailedRadioOption = ({ option, selectedValue, name, disabled, onChange }) => (
  <Box
    key={option.key}
    sx={{
      flex: '1 1 calc(33.333% - 16px)',
      minWidth: '200px',
    }}
  >
    <Paper
      elevation={selectedValue === option.key ? 4 : 1}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'space-between',
        border: 1,
        borderColor: selectedValue === option.key ? 'primary.main' : 'grey.300',
        backgroundColor: '#FFFFFF',
        height: '100%',
        p: 2,
        mt: 1,
        transition: 'border-color 0.3s, box-shadow 0.3s',
        ...(!disabled && {
          ':hover': {
            borderColor: 'primary.light',
            cursor: 'pointer',
            boxShadow: 3,
          },
        }),
      }}
      onClick={() => !disabled && onChange(option.key)}
    >
      <Box sx={{ textAlign: 'center', mb: 2 }}>
        <Typography variant="h6" component="div" sx={{ fontWeight: 'bold' }}>
          {option.label}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {option.detail}
        </Typography>
      </Box>
      <Radio
        disabled={disabled}
        checked={selectedValue === option.key}
        onChange={(e) => onChange(e.target.value)}
        value={option.key}
        name={name}
        sx={{ mt: 'auto' }}
      />
    </Paper>
  </Box>
);

DetailedRadioOption.propTypes = {
  option: PropTypes.object.isRequired,
  selectedValue: PropTypes.any,
  name: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const FormDetailedRadioGroup = ({
  name,
  label,
  options,
  disabled,
  rules,
}) => {
  const { control } = useFormContext();
  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field: { onChange, value }, fieldState: { error } }) => (
        <FormControl error={!!error} fullWidth>
          <FormLabel component="legend">{label}</FormLabel>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
            {options.map((option) => (
              <DetailedRadioOption
                key={option.key}
                option={option}
                selectedValue={value}
                name={name}
                disabled={disabled}
                onChange={onChange}
              />
            ))}
          </Box>
          {error && <FormHelperText>{error.message}</FormHelperText>}
        </FormControl>
      )}
    />
  );
};

FormDetailedRadioGroup.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  options: PropTypes.array.isRequired,
  disabled: PropTypes.bool,
  rules: PropTypes.object,
};

export default FormDetailedRadioGroup;
